/*
 * Single entry for every runtime context of the widget. The same bundle can run:
 *   1. inside the widget iframe (frame.html sets the frame flag) -> boot the real app,
 *      talking to the host shell over postMessage. Used in production AND for local
 *      iframe testing (`?iframe`), where Vite still serves the modules so HMR works.
 *   2. on a customer page in production -> define the <constell-widget> shell, which
 *      hosts the app in a cross-origin transparent iframe (full isolation from host-
 *      page scripts and CSS).
 *   3. on the local dev page (default) -> mount the app straight into the page so Vite
 *      HMR, Vue Devtools and the normal dev flow keep working exactly as before.
 *
 * The customer embed never changes across any of these.
 */
import { version } from '../package.json';
import { bootstrapApp } from '@/frame/frame-app';
import { isFramedContext } from '@/frame/bridge';
import { defineHostShell } from '@/frame/host-shell';

const release = `venue-manager-app@${version}`;

// "Am I the local Vite dev server?" — `import.meta.hot` is truthy ONLY when served by
// `vite dev`, and is statically stripped to `undefined` in every build (dev/stage/prod
// deploys alike), regardless of NODE_ENV. `import.meta.env.DEV` is NOT reliable here: it
// is derived from NODE_ENV, so deployed builds that build with a non-production NODE_ENV
// (our dev/stage jobs) would wrongly report DEV and mount in-page instead of the iframe.
const isDevServer = Boolean(import.meta.hot);

// Constell's own sites (star.constell.com and its dev/stage variants) embed the widget
// but must mount it directly in-page — never in the iframe. Any non-constell customer
// site gets the isolated iframe.
const isConstellHost = window.location.hostname.includes('constell.com');

// Escape hatch: `?iframe` exercises the real production iframe path on any of our own
// pages (dev server, playground, constell hosts). It wins over every inline shortcut.
const wantsIframe = new URLSearchParams(window.location.search)
    .has('iframe');

// Our own docs/playground page sets this so it always previews the real in-page
// webcomponent (never the customer iframe), regardless of which host serves it
// (prod, dev, CloudFront preview or a local static build).
const forceInline = Boolean((window as unknown as Record<string, unknown>).__CONSTELL_FORCE_INLINE__);

if (isFramedContext()) {
    bootstrapApp({
        bridged: true,
        release,
    });
} else if (!wantsIframe && (isDevServer || isConstellHost || forceInline)) {
    bootstrapApp({
        bridged: false,
        release,
    });
} else {
    // `import.meta.url` is the deployed (content-hashed) bundle URL; the shell resolves
    // `frame.html` and forwards this same bundle to the iframe relative to it.
    defineHostShell(import.meta.url);
}
